import { useQuery } from '@tanstack/react-query'
import type { VenueAliasRow } from 'pages/Desk/venueNameKey'
import { queryVenueAliases } from 'supabase/desk/queryVenueAliases'

// spec-editor-venue-matching §A (33.4 D-08): every venue alias, read once for
// the desk's venue matcher and the Remember offer's conflict check.
//
// The key is ['venueAliases'], deliberately OUTSIDE the 'desk' prefix (the
// same reason as useCustomVenueHistory): every desk decision and paste
// invalidates ['desk', ...], and setQueryDefaults(['desk']) turns focus and
// mount refetches on. Aliases change only when an admin remembers one, and
// the Remember offer (plan 33.4-11) invalidates this key after its write.

export const VENUE_ALIASES_QUERY_KEY = 'venueAliases'

/** Five minutes, like the venue history. */
const VENUE_ALIASES_STALE_TIME = 5 * 60_000

/** One instance, so a consumer's useMemo on `aliases` stays stable while there is no data. */
const NO_ROWS: VenueAliasRow[] = []

export interface VenueAliases {
  aliases: VenueAliasRow[]
  /**
   * The read failed (P-12). This does NOT join the desk's lookupFailed: the
   * desk keeps working with name-only matching, and the Remember offer hides
   * because its conflict check needs the aliases.
   */
  failed: boolean
  /**
   * The read answered (success or failure). The desk's prefill waits for it
   * (D-08): the form reads its initial values once, so it must open on the
   * venue an alias names, not on the name-only match of a read still in flight.
   */
  settled: boolean
}

/**
 * Never throws (P-12): an error or a rejected request becomes
 * `{ aliases: [], failed: true, settled: true }`. There is no `throwOnError` and no retry,
 * so a failing read costs one request. A database without the table answers
 * `{ aliases: [], failed: false, settled: true }` (queryVenueAliases).
 */
export const useQueryVenueAliases = (): VenueAliases => {
  const { data, isError, isSuccess } = useQuery<VenueAliasRow[]>({
    queryKey: [VENUE_ALIASES_QUERY_KEY],
    queryFn: queryVenueAliases,
    staleTime: VENUE_ALIASES_STALE_TIME,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })

  return { aliases: data ?? NO_ROWS, failed: isError, settled: isSuccess || isError }
}
